import { Chess, SQUARES, type Square } from "chess.js";
import type { Beat, Frame, Overlays, Tutorial } from "./types";

const SQUARE_SET = new Set<string>(SQUARES);

function checkSquares(o: Overlays | undefined, where: string) {
  if (!o) return;
  const bad = (s: string) => {
    if (!SQUARE_SET.has(s)) throw new Error(`${where}: "${s}" is not a square`);
  };
  o.arrows?.forEach((a) => {
    bad(a.from);
    bad(a.to);
  });
  o.squares?.forEach((s) => bad(s.sq));
  o.attacks?.forEach((a) => {
    bad(a.from);
    a.to?.forEach(bad);
  });
  o.labels?.forEach((l) => bad(l.sq));
  o.spotlight?.forEach(bad);
}

/**
 * Resolves every beat into the position it starts from and the move it plays.
 * Throws on an illegal move or an unknown square, so a broken lesson fails the
 * build instead of reaching readers. Move history carries across beats so that
 * repetition can be demonstrated.
 */
export function compileTutorial(t: Pick<Tutorial, "slug" | "startFen" | "beats">): Frame[] {
  let game = new Chess(t.startFen);
  let previousMove: Frame["previousMove"];
  return t.beats.map((beat: Beat, index) => {
    const where = `${t.slug} beat ${index + 1}`;
    if (beat.fen) {
      try {
        game = new Chess(beat.fen);
      } catch (e) {
        throw new Error(`${where}: bad FEN (${(e as Error).message})`);
      }
      previousMove = undefined;
    }
    checkSquares(beat, where);
    checkSquares(beat.after, where);
    const fen = game.fen();
    let move: Frame["move"];
    if (beat.move) {
      const u = beat.move;
      try {
        const m = game.move({ from: u.slice(0, 2), to: u.slice(2, 4), promotion: u[4] });
        move = { uci: m.lan, san: m.san, from: m.from, to: m.to, color: m.color };
      } catch {
        throw new Error(`${where}: illegal move ${u} in ${fen}`);
      }
    }
    const frame: Frame = { index, beat, fen, fenAfter: game.fen(), move, previousMove };
    if (move) previousMove = { from: move.from, to: move.to };
    return frame;
  });
}

/** Squares attacked by the piece on `from`, whoever is to move. */
export function attackedSquares(fen: string, from: Square): Square[] {
  const c = new Chess(fen);
  const p = c.get(from);
  if (!p) return [];
  return (SQUARES as Square[]).filter((s) => s !== from && c.attackers(s, p.color).includes(from));
}

/** Time a beat stays on screen at 1x speed: long enough to read, and to watch the move. */
export function beatDuration(beat: Beat): number {
  const words = beat.caption.split(/\s+/).filter(Boolean).length;
  const reading = 1400 + words * 290;
  const moving = beat.move ? 2200 : 0;
  return Math.max(beat.hold ?? 0, reading, moving, 2600);
}

/** Delay before the beat's move starts, so the camera and arrows arrive first. */
export const MOVE_DELAY = 900;
